import {
  Enum_Property_Publish_State,
  Maybe,
  Property,
  useGetAgentPropertyInterestCasesCountQuery,
  useGetAgentPropertyInterestCasesQuery
} from 'generated/graphql'
import React from 'react'

import {
  VStack,
  Flex,
  Heading,
  Text,
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Link,
  Center,
  Spinner
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

// import Swiper core and required modules
import SwiperCore, { Navigation, Thumbs } from 'swiper/core'
import moment from 'moment'
import PropertyCard from './PropertyCard'
import Pagination from 'components/Pagination'
import SmSearchBar from './SearchBar'
import { EmptyHandler } from 'components'
import SortOptions from './SortOptions'

SwiperCore.use([Navigation, Thumbs])

type PreviewPropertyCardProps = {
  property: Maybe<Property>
  onConfirm?: (id: Maybe<Property>) => Promise<void>
  refetchAll?: () => Promise<void>
}

const PreviewPropertyCard: React.FC<PreviewPropertyCardProps> = ({
  property,
  refetchAll,
  onConfirm
}) => {
  const [limit, setLimit] = React.useState(5)
  const [start, setStart] = React.useState(0)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [searchByActive, setSearchByActive] = React.useState<any>('full_name')
  const [activeSort, setActiveSort] = React.useState('created_at:desc')
  const { t, i18n } = useTranslation()
  const tanslator = i18n as any
  const lang = tanslator.language as string
  const variables = {
    where: {
      propertyId: property?.id || '',
      ...(searchTerm && {
        [`${[searchByActive]}_contains`]: searchTerm
      })
    }
  }
  const { data: interestCases, loading: interestCaseLoading } =
    useGetAgentPropertyInterestCasesQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        ...variables,
        limit,
        start,
        sort: activeSort
      },
      onError: (error) => {
        console.log(error)
      }
    })
  const { data: intersetCaseCount } = useGetAgentPropertyInterestCasesCountQuery({
    fetchPolicy: 'cache-and-network',
    variables
  })

  return (
    <>
      <Box bg="white" py={0}>
        <PropertyCard property={property} onConfirm={onConfirm} refetchAll={refetchAll} inline />
        {property?.publish_state === Enum_Property_Publish_State.Active && (
          <Link
            isExternal
            href={`/property/${property?.id}`}
            _hover={{
              textDecoration: 'none',
              bg: 'gray.100',
              color: 'blue.500'
            }}
          >
            <Text color="blue.500" fontSize="sm" py={5} px={8}>
              View property
            </Text>
          </Link>
        )}
      </Box>

      <Accordion w="100%" allowToggle borderColor="transparent" defaultIndex={[0]}>
        <AccordionItem>
          <h2>
            <AccordionButton p={6}>
              <Heading as="span" flex="1" size="sm" textAlign="left">
                Customer Interests ({intersetCaseCount?.getAgentPropertyInterestCasesCount || 0})
              </Heading>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel px={6}>
            <VStack spacing={0} alignItems="flex-start">
              <SmSearchBar
                placeholder={`Search by ${searchByActive}`}
                setSearchBy={setSearchByActive}
                options={
                  [
                    { value: 'full_name', label: 'Name' },
                    { value: 'email', label: 'Email' },
                    { value: 'cell_number', label: 'Phone Number' },
                    { value: 'caseID', label: 'Case ID' }
                  ] as any
                }
                onSearch={(text) => {
                  setSearchTerm(text)
                }}
              />
              <SortOptions
                options={[
                  { value: 'created_at:desc', label: 'Newest' },
                  { value: 'created_at:asc', label: 'Oldest' }
                ]}
                activeSort={activeSort}
                setActiveSort={setActiveSort}
              />
              {interestCaseLoading ? (
                <Center w="100%">
                  <Spinner />
                </Center>
              ) : (intersetCaseCount?.getAgentPropertyInterestCasesCount || 0) === 0 ? (
                <EmptyHandler title="No interest Case found" bg="transparent" />
              ) : (
                interestCases?.getAgentPropertyInterestCases?.map((request) => (
                  <Box
                    key={request?.id}
                    borderRadius="xl"
                    width="100%"
                    py={4}
                    borderBottom="1px solid"
                    borderBottomColor="gray.100"
                  >
                    <Text fontSize="xx-small" color="blue.400" fontWeight="extrabold">
                      {request?.caseID}
                    </Text>
                    <Heading size="sm">{request?.full_name}</Heading>
                    <Text fontWeight="semibold" fontSize="xs">
                      {request?.email}
                    </Text>
                    <Text fontWeight="semibold" color="orange.500" fontSize="sm">
                      {request?.cell_number}
                    </Text>
                    <Text fontSize="xs">{request?.message}</Text>
                    <Text my={2} fontSize="xs">
                      {moment(request?.created_at).fromNow()}
                    </Text>
                  </Box>
                ))
              )}
            </VStack>
            <Pagination
              count={intersetCaseCount?.getAgentPropertyInterestCasesCount || 0}
              my={0}
              mt={16}
              limit={limit}
              setLimit={setLimit}
              start={start}
              setStart={setStart}
            />
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <h2>
            <AccordionButton p={6}>
              <Heading as="span" flex="1" size="sm" textAlign="left">
                Property Details
              </Heading>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel fontSize="sm" px={6}>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Property type
              </Text>
              <Text fontSize="sm">{property?.property_category?.name}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Lease period
              </Text>
              <Text fontSize="sm">{property?.lease_period}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Type of property
              </Text>
              <Text fontSize="sm">
                {(property?.type_of_property as any)?.[`name_${lang}`] || ''}
              </Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Rental term
              </Text>
              <Text fontSize="sm">{(property?.rental_term as any)?.[`name_${lang}`] || ''}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Bedrooms
              </Text>
              <Text fontSize="sm">{property?.bedrooms}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Bathrooms
              </Text>
              <Text fontSize="sm">{property?.bathrooms}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Parking
              </Text>
              <Text fontSize="sm">{property?.parking}</Text>
            </Flex>
            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Floor size
              </Text>
              <Text fontSize="sm">{property?.floor_size}</Text>
            </Flex>

            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Property availability
              </Text>
              <Text fontSize="sm">
                {(property?.property_availability as any)?.[`name_${lang}`] || ''}
              </Text>
            </Flex>

            <Flex justify="space-between" align="center">
              <Text fontWeight="semibold" fontSize="sm">
                Furnished
              </Text>
              <Text fontSize="sm">{property?.furnished ? t('yes') : t('no')}</Text>
            </Flex>
          </AccordionPanel>
        </AccordionItem>
        <AccordionItem>
          <h2>
            <AccordionButton p={6}>
              <Heading as="span" flex="1" size="sm" textAlign="left">
                Description
              </Heading>
              <AccordionIcon />
            </AccordionButton>
          </h2>
          <AccordionPanel fontSize="sm" px={6}>
            {property?.description}
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  )
}

export default PreviewPropertyCard
